<template>
  <div class="target-notification-select-block">
    <div v-if="isDefault">
      <BaseElFormItem label="所有服務項目均會收到此通知" :show-message="false">
        <p class="hint-text">如需發送不一樣的通知內容，給限定的對象，請返回通知列表頁，新增一則通知</p>
      </BaseElFormItem>
    </div>

    <div v-else>
      <BaseElFormItem label="限定的服務項目將收到此通知" prop="NotifyTargetTemplateId">
        <template slot="label">
          <div class="flex justify-between w-[560px]">
            <div>限定的服務項目將收到此通知</div>
            <div v-if="syncValue" class="text-sub text-gray-50 underline" @click="showDialog = true">查看範本包含的通知對象</div>
          </div>
        </template>
        <BaseElSelect v-model="syncValue" value-key="id">
          <BaseElSelectOption
            v-for="option in templateOptions"
            :key="option.value"
            :value="option.value"
            :label="option.label"
          />
        </BaseElSelect>
        <p v-if="hiddenByNotifyType" class="hint-text">設有訂單風險條件的範本不支援此通知類型，因訂單風險於預約建立後才完成評估；請改用其他通知類型</p>
        <p v-else-if="hiddenByRiskLevelFeature" class="hint-text">現有的通知對象範本皆設有訂單風險條件，需啟用預約風控守門員模組才能選擇</p>
      </BaseElFormItem>
      <p class="hint-text">如需調整所有對象「均會」收到的通知，請調整「預設通知」</p>
    </div>

    <TargetNotificationSelectModal
      v-if="showDialog"
      :targetTemplate="selectedTemplate"
      :reservationServicesData="reservationServicesData"
      @close="showDialog = false"
    />
  </div>
</template>

<script>
import { useVModel } from '@vueuse/core'
import { defineComponent, ref, computed, onMounted } from 'vue'
import { map, filter, get, isEmpty, find } from 'lodash'
import { useRoute } from 'vue-router/composables'
import TargetNotificationSelectModal from './TargetNotificationSelectModal.vue'
import { GetNotifyTargetTemplateAppointmentConfigOptions } from '@/api/progressNotification'
import { useFetch } from '@/use/fetch'
import { useShop } from '@/use/shop'
import { usePermissions } from '@/use/permissions'

export default defineComponent({
  name: 'TargetNotificationSelectBlock',
  components: { TargetNotificationSelectModal },
  props: {
    value: { type: String },
    targetTemplateList: { type: Array, default: () => [] },
    isDefault: { type: Boolean },
  },
  setup (props, { emit }) {
    const route = useRoute()
    const syncValue = useVModel(props, 'value', emit)
    const { simpleFetch } = useFetch()
    const { shopId } = useShop()
    const { checkAction } = usePermissions()
    const showDialog = ref(false)
    const reservationServicesData = ref({})

    // 檢查範本是否為風險等級範本
    const isRiskLevelTemplate = (template) => {
      const riskLevelConditions = get(template, 'appointmentConfig.riskLevelConditions')
      return !isEmpty(riskLevelConditions)
    }

    // 檢查用戶是否有風險等級功能權限
    const hasRiskLevelFeature = computed(() => {
      return checkAction('planModule.appointmentRiskGuard.enable')
    })

    // 當前通知類型 (從路由參數取得)
    const notifyType = computed(() => route.params.type)

    // 後端 checkMatchNotifyTargetTemplate 不分通知類型，風險等級條件對任何通知類型都會生效，
    // 唯獨「會員建立預約」有時序問題：AppointmentOrder.riskLevel 是預約建立後由
    // shopEventPolicy → assessRisk 非同步寫入的，該通知觸發時可能還是 null 而靜默不命中，
    // 因此只排除這一種類型（原本寫死只有 appointmentStart 支援，把風險範本從其餘 4 種類型全濾掉）
    const unsupportedRiskLevelNotifyTypes = ['appointmentCreate']
    const supportsRiskLevel = computed(
      () => !unsupportedRiskLevelNotifyTypes.includes(notifyType.value),
    )

    // 篩選範本選項
    const templateOptions = computed(() => {
      let templates = props.targetTemplateList

      if (!supportsRiskLevel.value || !hasRiskLevelFeature.value) {
        templates = filter(templates, template => !isRiskLevelTemplate(template))
      }

      return map(templates, template => ({
        id: template.id,
        label: template.name,
        value: template.id,
        isRiskLevel: isRiskLevelTemplate(template),
      }))
    })

    // 有風險範本被濾掉時給明確提示，避免只顯示「無資料」讓店家以為系統壞掉
    const filteredRiskLevelTemplates = computed(() => {
      if (isEmpty(props.targetTemplateList)) return []
      return filter(props.targetTemplateList, isRiskLevelTemplate)
    })

    const hiddenByNotifyType = computed(() => {
      if (supportsRiskLevel.value) return false
      return !isEmpty(filteredRiskLevelTemplates.value)
    })

    const hiddenByRiskLevelFeature = computed(() => {
      if (hasRiskLevelFeature.value) return false
      if (isEmpty(props.targetTemplateList)) return false
      return props.targetTemplateList.every(isRiskLevelTemplate)
    })

    const selectedTemplate = computed(() => {
      return find(props.targetTemplateList, { id: syncValue.value })
    })

    onMounted(async () => {
      simpleFetch(GetNotifyTargetTemplateAppointmentConfigOptions, { shopId: shopId.value }, (res) => {
        console.log(res)
        reservationServicesData.value = res
      })
    })

    return {
      syncValue,
      showDialog,
      templateOptions,
      selectedTemplate,
      reservationServicesData,
      hiddenByNotifyType,
      hiddenByRiskLevelFeature,
    }
  },
})
</script>

<style lang="postcss" scoped>
.target-notification-select-block {
    @apply bg-white rounded-[8px] p-[20px];
}

.hint-text {
    @apply text-sm text-gray-60;
}

::v-deep .el-form-item {
  @apply mb-0;
}
</style>
